import { STRIPE_URL } from "@core/constants";
import { STRIPE_PK } from "@core/constants/env.constants";
import { NotificationsManager } from "@core/managers/notifications.manager";
import { JavascriptHelpers, StripeHelpers } from "@core/helpers";
import { Logger } from "@core/helpers/logger.helpers";

/**
 * Fetch Stripe instance in global namespace
 * @class StripeService
 */
export class StripeService {
  static async getPaymentIntent(clientIntent) {
    await StripeService.loadStripe();
    const stripeInstance = window.Stripe && window.Stripe(STRIPE_PK);
    const { error, paymentIntent } = (await stripeInstance.handleCardAction(clientIntent)) || {};
    return { error, paymentIntent };
  }

  static async loadStripe() {
    /* eslint-disable-next-line consistent-return */
    return new Promise((resolve, reject) => {
      try {
        if (!JavascriptHelpers.hasScript(STRIPE_URL)) {
          Logger.log("Stripe - no script detected, loading Stripe");

          const uri = STRIPE_URL;
          const callback = () => {
            Logger.log("Stripe - script loaded");
            return resolve();
          };

          JavascriptHelpers.loadScript({ uri, callback });
        } else {
          return resolve();
        }
      } catch (error) {
        NotificationsManager.contactEtabSupport();
        Logger.error(error);
        return reject(error);
      }
    });
  }

  static async stripeRedirect(id) {
    try {
      await StripeService.loadStripe();
      const stripeInstance = window.Stripe && window.Stripe(STRIPE_PK);
      /**
       * @deprecated https://stripe.com/docs/js/deprecated/redirect_to_checkout
       */
      const { error } = await stripeInstance.redirectToCheckout({ sessionId: id });
      StripeHelpers.errorHandler({ error });
    } catch (error) {
      throw error;
    }
  }

  static async getKycAccountResult(account) {
    try {
      await StripeService.loadStripe();
      const stripeInstance = window.Stripe && window.Stripe(STRIPE_PK);
      const { error, token } = await stripeInstance.createToken(account.getType(), account.getData());
      StripeHelpers.errorHandler({ error });
      return token;
    } catch (error) {
      throw error;
    }
  }

  static async sendFileToStripe({ file, fileData }) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("purpose", fileData.purpose);
      const response = await fetch("https://uploads.stripe.com/v1/files", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${process.env.VUE_APP_STRIPE_PK}`,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Error sending file to Stripe");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async getAccountResult({ data, fileData }) {
    try {
      await StripeService.loadStripe();
      const stripeInstance = window.Stripe && window.Stripe(STRIPE_PK);
      const parameter = fileData.verification_parameter;
      const parameterType = fileData.verification_parameter_type;
      const { error, token } = await stripeInstance.createToken("person", {
        person: {
          verification: {
            [parameter]: {
              [parameterType]: data.id,
            },
          },
        },
      });
      StripeHelpers.errorHandler({ error });
      return token;
    } catch (error) {
      throw error;
    }
  }
}
